import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 120px 0 80px;
  display: flex;
  align-items: center;
  min-height: 100vh;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 100px 0 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 4rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 600px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled(motion.h2)`
  font-size: 2rem;
  font-weight: 400;
  margin-bottom: 1.5rem;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeroDescription = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 3rem;
  opacity: 0.8;
  line-height: 1.6;
`;

const HeroStats = styled(motion.div)`
  display: flex;
  gap: 3rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatNumber = styled.span`
  display: block;
  font-size: 2.5rem;
  font-weight: 700;
  color: #fbbf24;
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeroPlaceholder = styled(motion.div)`
  background: rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 4rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);

  i {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.8;
  }

  p {
    font-size: 1.25rem;
    opacity: 0.8;
  }
`;

const Hero = () => {
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true
  });

  const [counters, setCounters] = useState({
    events: 0,
    organizations: 0,
    attendees: 0
  });

  useEffect(() => {
    if (inView) {
      const targets = { events: 120, organizations: 250, attendees: 15000 };
      const duration = 2000;
      
      Object.keys(targets).forEach((key, index) => {
        setTimeout(() => {
          animateCounter(key, targets[key], duration);
        }, index * 200);
      });
    }
  }, [inView]);

  const animateCounter = (key, target, duration) => {
    let start = 0;
    const increment = target / (duration / 16);
    
    const updateCounter = () => {
      start += increment;
      if (start < target) {
        setCounters(prev => ({
          ...prev,
          [key]: Math.floor(start)
        }));
        requestAnimationFrame(updateCounter);
      } else {
        setCounters(prev => ({
          ...prev,
          [key]: target
        }));
      }
    };
    
    updateCounter();
  };

  return (
    <HeroSection ref={ref}>
      <Container>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Tech Week Grand Rapids 2025
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Recap & 2026 Planning
          </HeroSubtitle>
          
          <HeroDescription
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Building on our record-breaking success to create an even more impactful Tech Week 2026
          </HeroDescription>
          
          <HeroStats
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Stat>
              <StatNumber>{counters.events}+</StatNumber>
              <StatLabel>Events</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>{counters.organizations}+</StatNumber>
              <StatLabel>Organizations</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>{counters.attendees.toLocaleString()}+</StatNumber>
              <StatLabel>Attendees</StatLabel>
            </Stat>
          </HeroStats>
        </HeroContent>
        
        <HeroImage>
          <HeroPlaceholder
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <i className="fas fa-users"></i>
            <p>Tech Week 2025 Community</p>
          </HeroPlaceholder>
        </HeroImage>
      </Container>
    </HeroSection>
  );
};

export default Hero;
